/** @jsxImportSource @emotion/react */
import React, { useMemo, useState, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
} from "recharts";
import { Text } from "@phoenix/components";
import {
    ChartTooltip,
    ChartTooltipItem,
    defaultCartesianGridProps,
    defaultLegendProps,
    defaultXAxisProps,
    defaultYAxisProps,
    useBinInterval,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import type { ToolUsageSummaryQuery } from "./__generated__/ToolUsageSummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
`;

const widgetHeaderCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const selectCSS = css`
  background: var(--ac-global-color-grey-100, rgba(0,0,0,0.05));
  border: 1px solid var(--ac-global-color-grey-300, rgba(0,0,0,0.1));
  border-radius: 8px;
  color: var(--ac-global-text-color-900);
  font-size: 13px;
  padding: 6px 12px;
  cursor: pointer;
  outline: none;
  min-width: 150px;
  &:hover {
    border-color: var(--ac-global-color-primary);
  }
`;

const chartContainerCSS = css`
  flex: 1;
  min-height: 250px;
`;

const COLOR_PALETTE = ["#1e40af", "#0e7490", "#047857", "#6d28d9", "#be185d", "#b45309", "#be123c"];

function CustomTooltip({
    active,
    payload,
    label,
    scale,
}: TooltipContentProps<number, string> & { scale?: string }) {
    if (active && payload && payload.length) {
        const date = new Date(label || "");
        const isDayScale = scale === "DAY";
        const timeStr = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(isDayScale ? {} : { hour: '2-digit', minute: '2-digit', hour12: true })
        });

        return (
            <ChartTooltip>
                <div style={{ marginBottom: 4, fontWeight: 700, fontSize: 12 }}>{timeStr}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {payload.map((p, i) => (
                        <ChartTooltipItem
                            key={i}
                            color={p.color || "#6366f1"}
                            shape="circle"
                            name={p.name || ""}
                            value={intShortFormatter(p.value as number)}
                        />
                    ))}
                </div>
            </ChartTooltip>
        );
    }
    return null;
}

export function ToolUsageSummary({ projectId, timeRange, fetchKey, widgetId, widgetTitle, onDataExport }: ProjectMetricViewProps) {
    const [selectedTool, setSelectedTool] = useState("all");

    const defaultScale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    // Machi, respect the aggregation! Week/Month/Day (5-35 days) should stay as DAY for cleaner views
    const scale = useMemo(() => {
        if (defaultScale === "MONTH" || defaultScale === "WEEK" || defaultScale === "DAY") return "DAY";
        return defaultScale;
    }, [defaultScale]);

    const data = useLazyLoadQuery<ToolUsageSummaryQuery>(
        graphql`
      query ToolUsageSummaryQuery($projectId: ID!, $timeRange: TimeRange!, $timeBinConfig: TimeBinConfig!, $filterCondition: String!) {
        project: node(id: $projectId) {
          ... on Project {
            spanCountTimeSeries(timeRange: $timeRange, timeBinConfig: $timeBinConfig, filterCondition: $filterCondition) {
              data { timestamp okCount errorCount unsetCount }
            }
            # Fetch spans to identify unique tool names
            spans(timeRange: $timeRange, filterCondition: $filterCondition, first: 10000) {
              edges {
                node {
                  name
                  startTime
                }
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: { start: timeRange.start?.toISOString(), end: timeRange.end?.toISOString() },
            timeBinConfig: { scale, utcOffsetMinutes },
            filterCondition: 'span_kind == "TOOL"',
        },
        { fetchPolicy: "network-only", fetchKey }
    );

    const toolNames = useMemo(() => {
        const names = new Set<string>();
        data.project?.spans?.edges?.forEach((e: any) => {
            if (e.node?.name) names.add(e.node.name);
        });
        return Array.from(names).sort();
    }, [data.project?.spans?.edges]);

    // The COLOR_PALETTE constant was duplicated, removing the second one.
    // const COLOR_PALETTE = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#f43f5e", "#06b6d4"];

    const chartData = useMemo(() => {
        const timeSeries = data.project?.spanCountTimeSeries?.data ?? [];
        const spans = data.project?.spans?.edges ?? [];

        // Map spans to their nearest bucket
        const bucketMap: Record<string, Record<string, number>> = {};

        spans.forEach((e: any) => {
            const node = e.node;
            if (!node?.startTime) return;

            const date = new Date(node.startTime);
            let binTime: number;

            if (scale === "DAY") {
                binTime = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
            } else if (scale === "HOUR") {
                binTime = new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours()).getTime();
            } else {
                // Approximate for others if needed, but dashboard mostly uses DAY/HOUR
                binTime = date.getTime();
            }

            // Find the closest bucket in timeSeries
            let closestTimestamp = "";
            let minDiff = Infinity;

            timeSeries.forEach((d: any) => {
                const ts = new Date(d.timestamp).getTime();
                const diff = Math.abs(ts - binTime);
                if (diff < minDiff) {
                    minDiff = diff;
                    closestTimestamp = d.timestamp;
                }
            });

            if (closestTimestamp && minDiff < (scale === "DAY" ? 43200000 : 1800000)) { // 12h or 30m grace
                if (!bucketMap[closestTimestamp]) bucketMap[closestTimestamp] = {};
                const name = node.name || "unknown";
                bucketMap[closestTimestamp][name] = (bucketMap[closestTimestamp][name] || 0) + 1;
            }
        });

        return timeSeries.map((d: any) => {
            const point: any = {
                timestamp: d.timestamp,
                _total: (d.okCount || 0) + (d.errorCount || 0) + (d.unsetCount || 0)
            };

            const usage = bucketMap[d.timestamp] || {};
            toolNames.forEach(name => {
                point[name] = usage[name] || 0;
            });
            return point;
        });
    }, [data, scale, toolNames]);

    useEffect(() => {
        if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
            onDataExport(widgetId, widgetTitle, chartData);
        }
    }, [chartData, onDataExport, widgetId, widgetTitle]);

    const interval = useBinInterval({ scale });

    return (
        <div css={containerCSS}>
            <div css={chartContainerCSS}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 20 }}>
                        <defs>
                            {toolNames.map((name, i) => (
                                <linearGradient key={name} id={`grad-tool-${i}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={COLOR_PALETTE[i % COLOR_PALETTE.length]} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={COLOR_PALETTE[i % COLOR_PALETTE.length]} stopOpacity={0.15} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid {...defaultCartesianGridProps} vertical={false} strokeDasharray="3 3" opacity={0.3} />
                        <XAxis
                            {...defaultXAxisProps}
                            dataKey="timestamp"
                            tickFormatter={(x) => {
                                const d = new Date(x);
                                if (scale === "DAY") {
                                    return d.toLocaleString('en-US', { month: 'numeric', day: 'numeric' });
                                }
                                return d.toLocaleString("en-US", { month: "numeric", day: "numeric", hour: "2-digit" });
                            }}
                            tick={{ fontSize: 10, fill: "var(--ac-global-text-color-500)" }}
                            interval={interval}
                        />
                        <YAxis {...defaultYAxisProps} width={40} tick={{ fontSize: 10 }} tickFormatter={intShortFormatter} />
                        <Tooltip content={(props: any) => <CustomTooltip {...props} scale={scale} />} />

                        {toolNames.map((name, i) => (
                            <Area
                                key={name}
                                type="monotone"
                                dataKey={name}
                                stackId="1"
                                stroke={COLOR_PALETTE[i % COLOR_PALETTE.length]}
                                fill={`url(#grad-tool-${i})`}
                                strokeWidth={2.5}
                                dot={{ fill: "#eab308", stroke: "#fff", strokeWidth: 1.5, r: 4 }}
                                activeDot={{ r: 7, stroke: "#fff", strokeWidth: 2 }}
                                connectNulls
                            />
                        ))}
                        <Legend {...defaultLegendProps} iconType="circle" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
